import { useFlags } from 'launchdarkly-react-client-sdk';
import { cloneDeep } from 'lodash';
import { useEffect, useState, useMemo, useCallback } from 'react';
import { useSnapshot } from 'valtio';
import useScreenCepTargetsConfig from './MiViewScreenCepTargets.config';
import { TacticStatus } from 'basics/enums/maya.enums';
import useGetAllTacticsQuery from 'basics/graphql/queries/getAllTactics';
import useGetTargetListByCepIdQuery from 'basics/graphql/queries/getTargetListByCepId';
import { transformTacticTitle } from 'basics/transformers/Tactic.transformer';
import { CepStateType } from 'basics/types/maya.types';
import { Plan } from 'components/MiViewPreview/MiViewPlanSelector/MiViewPlanSelector.types';
import { Target, Tactic } from 'generated/maya.types';
import cepState from 'states/cep.states';

const useMiViewScreenCepTargets = (fullScreen: boolean, setFullScreen: Function) => {
  const cepStateValue = useSnapshot(cepState) as CepStateType;
  const flags = useFlags();

  const [targets, setTargets] = useState<Target[]>([]);
  const [tactics, setTactics] = useState<Tactic[]>([]);
  const [screenLoading, setScreenLoading] = useState(true);
  const [selectedAccount, setSelectedAccount] = useState<Target | undefined>();
  const [openedAccordion, setOpenedAccordion] = useState(true);

  const cep = cepStateValue?.cep;
  const cepId = cep?._id ?? null;

  const queryTacticFilters = useMemo(
    () => ({
      cep: cepId,
      status: [TacticStatus.EMPTY, TacticStatus.DRAFT, TacticStatus.APPROVED],
    }),
    [cepId],
  );

  const { data, loading: targetsLoading } = useGetTargetListByCepIdQuery(cepId);
  const { data: tacticsData, loading: tacticsLoading } = useGetAllTacticsQuery(undefined, queryTacticFilters);

  const sortedTargets = useMemo(() => {
    if (!data) return [];
    const targetsList = cloneDeep(data);
    return targetsList.sort((a, b) => `${a.firstName || a.lastName}`.localeCompare(`${b.firstName || b.lastName}`));
  }, [data]);

  const transformedTactics = useMemo(() => {
    if (!tacticsData) return [];
    const tacticsList = cloneDeep(tacticsData);
    const sorted = tacticsList.sort(
      (a, b) => Number(a.actionOrder) - Number(b.actionOrder),
    );
    return sorted.map((t) => transformTacticTitle(t, flags));
  }, [tacticsData, flags]);

  useEffect(() => {
    setTargets(sortedTargets);
    setTactics(transformedTactics);
    setScreenLoading(targetsLoading || tacticsLoading);
  }, [sortedTargets, transformedTactics, targetsLoading, tacticsLoading]);

  const collapseAllAction = useCallback(() => {
    setOpenedAccordion(false);
  }, []);

  const expandAllAction = useCallback(() => {
    setOpenedAccordion(true);
  }, []);

  const fullScreenAction = useCallback(() => setFullScreen(true), [setFullScreen]);
  const minimizeScreenAction = useCallback(() => setFullScreen(false), [setFullScreen]);

  const { screenCepTargetsConfig } = useScreenCepTargetsConfig(
    collapseAllAction,
    expandAllAction,
    fullScreenAction,
    minimizeScreenAction,
    fullScreen,
  );

  const plans = useMemo<Plan[]>(() => {
    if (!cep) return [];
    return [{ id: cep._id ?? null, name: cep.title ?? null }];
  }, [cep]);

  const defaultPlan = useMemo<Plan>(() => {
    return { id: cep?._id ?? null, name: cep?.title ?? null };
  }, [cep]);

  return {
    targets,
    tactics,
    cep,
    screenLoading,
    plans,
    defaultPlan,
    selectedAccount,
    setSelectedAccount,
    screenCepTargetsConfig,
    openedAccordion,
  };
};

export default useMiViewScreenCepTargets;
